import { useNavigate } from "react-router-dom";
import { ArrowRight, Play, Award, Clock } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Landing() {
  const nav = useNavigate();
  const { user, latest } = useAuth();

  const ctaPrimary = () => {
    if (user) nav(latest ? "/skor" : "/test");
    else nav("/kayit", { state: { next: "/test" } });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-testid="landing-page">
      <section className="lg:col-span-6 pt-6 lg:pt-12">
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] tracking-tight h-gradient">
          Hayatının hangi alanı seni geride tutuyor?
        </h1>
        <p className="mt-7 text-neutral-400 text-base md:text-lg max-w-xl leading-relaxed">
          Life 101 Yaşam Skoru ile 10 temel alanda kendini ölç, eksiklerini gör ve sana özel kaynakları keşfet.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button className="btn-primary inline-flex items-center gap-2" data-testid="start-quiz-btn" onClick={ctaPrimary}>
            {user ? (latest ? "Skorumu Gör" : "Testi Başlat") : "Üye Ol & Teste Başla"} <ArrowRight size={18} />
          </button>
          <button className="btn-secondary inline-flex items-center gap-2" data-testid="how-it-works-btn" onClick={() => nav("/nasil-calisir")}>
            <Play size={16} /> Nasıl Çalışır?
          </button>
        </div>

        {user && (
          <div className="mt-6 text-sm text-neutral-400" data-testid="signed-in-hint">
            {latest ? `Son skorun: ${latest.general_score}/100 — istersen tekrar çözebilirsin.` : "Hesabın hazır — teste başlayabilirsin."}
          </div>
        )}

        <div className="mt-14 grid grid-cols-2 gap-4 max-w-md">
          <div className="glass p-5 flex items-center gap-3">
            <Award className="text-neutral-300" size={22} />
            <div>
              <div className="text-white font-semibold">10 alanlı</div>
              <div className="text-xs text-neutral-500">kişisel analiz</div>
            </div>
          </div>
          <div className="glass p-5 flex items-center gap-3">
            <Clock className="text-neutral-300" size={22} />
            <div>
              <div className="text-white font-semibold">7-10 dakikalık</div>
              <div className="text-xs text-neutral-500">test</div>
            </div>
          </div>
        </div>
      </section>

      <section className="lg:col-span-6">
        {user && latest ? (
          <div className="glass p-8 relative overflow-hidden" data-testid="last-score-widget">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-neutral-400 uppercase tracking-[0.15em]">Son Skorun</div>
              <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                {new Date(latest.completed_at).toLocaleDateString("tr-TR", { day: "numeric", month: "short" })}
              </span>
            </div>
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="text-7xl font-bold h-gradient leading-none">
                  {latest.general_score}<span className="text-2xl text-neutral-500 align-top ml-1">/100</span>
                </div>
                <div className="mt-3 flex items-center gap-2 text-neutral-400 text-sm">
                  <span className="w-2 h-2 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                  {latest.general_score >= 80 ? "Çok iyi seviyedesin" : latest.general_score >= 60 ? "İyi seviyedesin" : latest.general_score >= 40 ? "Gelişime açıksın" : "Acil gelişim alanı"}
                </div>
              </div>
              <div className="relative w-28 h-28">
                <div className="absolute inset-0 rounded-full border-[6px] border-neutral-900" />
                <div className="absolute inset-0 rounded-full border-[6px] border-transparent" style={{ borderTopColor: "#fff", borderRightColor: "#fff", transform: `rotate(${45 + (latest.general_score / 100) * 270}deg)`, boxShadow: "0 0 28px rgba(255,255,255,0.5)", transition: "transform 600ms ease" }} />
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Gelişime Açık 3 Alan</div>
              {(latest.development_areas || []).slice(0, 3).map((label) => (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-neutral-300">{label}</span>
                  <span className="text-neutral-500">öncelik</span>
                </div>
              ))}
            </div>

            <div className="mt-7 flex gap-3">
              <button onClick={() => nav("/panelim")} className="btn-primary text-sm flex-1" data-testid="goto-panel-btn">
                Panelime Git
              </button>
              <button onClick={() => nav("/test")} className="btn-secondary text-sm" data-testid="retake-quiz-btn">Tekrar Çöz</button>
            </div>
          </div>
        ) : (
          <div className="glass p-8 relative overflow-hidden">
            <div className="text-sm text-neutral-400 uppercase tracking-[0.15em] mb-4">Yaşam Skoru Önizleme</div>
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="text-7xl font-bold h-gradient leading-none">—<span className="text-2xl text-neutral-500 align-top ml-1">/100</span></div>
                <div className="mt-3 text-neutral-400 text-sm">Skorunu testten sonra göreceksin</div>
              </div>
              <div className="relative w-28 h-28">
                <div className="absolute inset-0 rounded-full border-[6px] border-neutral-900" />
                <div className="absolute inset-0 rounded-full border-[6px] border-transparent" style={{ borderTopColor: "#fff", borderRightColor: "#fff", transform: "rotate(45deg)", boxShadow: "0 0 28px rgba(255,255,255,0.5)" }} />
              </div>
            </div>
            <div className="space-y-3">
              {["Disiplin", "Odak", "Para", "Özgüven", "Zihin"].map((label) => (
                <div key={label}>
                  <div className="flex justify-between text-sm mb-1.5"><span className="text-neutral-300">{label}</span><span className="text-neutral-600">— / 100</span></div>
                  <div className="score-bar"><div style={{ width: "0%" }} /></div>
                </div>
              ))}
            </div>
            <p className="text-xs text-neutral-500 mt-6">Üye ol, 50 soruyu yanıtla ve 10 alanın tamamı için skorunu, raporunu ve sana özel kaynakları gör.</p>
          </div>
        )}
      </section>
    </div>
  );
}
